import { IAdsToEmbed, IAdToEmbed, IArtSponsParams, ServiceSlotType, TargetingParamsType, TargetingType } from '../ad-services.types';
import {
    checkTargetingSetting,
    getArtSponsParamsFromService,
    getPlacementNamesToDestroy,
    groupExecution,
    setArtSponsParamsFromService,
} from '../ad-services.utils';
import { IDefinedAdUnits, IDFPService, IDFPServiceSlot, IServiceDFPUtils } from './service-dfp.types';
import { AD_SERVERS } from '../../../configs/constants';
import {
    defineAdUnit,
    embedAdSlotWhenIsVisible,
    getDefinedSlotName,
    getSlotSettings,
    renderDefinedAdUnits,
    setTargetingBeforeRefresh,
} from './embedding-dfp.utils';
import DebugUANC from '../../../core/version/debug';
import { requestForBids } from '../../prebid/prebid';
import { isSlotCanBeEmbed, verifySlotsToDefined } from '../embedding.utils';

/**
 * Inicjuje osadzanie zgrupowanych slotów. Przekazuje pojedyncze placementy do zdefiniowania. Następnie zdefiniowane jednostki przekazuje dalej do prebida i wyświetlenia reklamy.
 *
 * @param {IAdsToEmbed} adsToEmbed - sloty ze wskazanymi placementami do osadzenia
 * @returns {void}
 */
const insertAds = async (adsToEmbed: IAdsToEmbed): Promise<void> => {
    const { UANC, services } = window.Inpl.Ad;
    const service = services?.DFPService as IDFPService;

    const slotNames = Object.keys(adsToEmbed);
    const slotConfigs = service.slots.filter((slotConfig) => slotNames.includes(slotConfig.name));
    const slotsToDefined = verifySlotsToDefined(slotConfigs);
    if (!slotsToDefined.length) return;

    // przygotowanie promises definiujących jednostki reklamowe
    const defineAdUnitPromises: Promise<string>[] = slotsToDefined.reduce((promisesArr: Promise<string>[], slotConfig) => {
        const { customKeywords, slotMapping } = getSlotSettings(slotConfig);

        const adUnits = adsToEmbed[slotConfig.name].reduce((definePromises: Promise<string>[], adToEmbed: IAdToEmbed) => {
            const defineAdUnitPromises = adToEmbed.placementIds.map((placementId) =>
                defineAdUnit(slotConfig, placementId, customKeywords, slotMapping, adToEmbed.targeting)
            );
            return [...definePromises, ...defineAdUnitPromises];
        }, []);

        return [...promisesArr, ...adUnits];
    }, []);

    const definedAdUnits: IDefinedAdUnits = {};
    // definiowanie jednostek, informacja o problemach i niezdefiniowanych jednostkach
    await Promise.allSettled(defineAdUnitPromises).then((results) => {
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                const slotName = getDefinedSlotName(adsToEmbed, result.value);
                if (!slotName) return;
                definedAdUnits[slotName] = definedAdUnits[slotName] || [];
                definedAdUnits[slotName].push(result.value);
            } else {
                UANC instanceof DebugUANC && UANC.warn(`[defineAdUnit] ${result.reason.message}`);
            }
        });
    });
    UANC instanceof DebugUANC && UANC.table('Successfully defined Ad Units:', definedAdUnits);

    // przekazanie zdefiniowanych jednostek do osadzenia - prebid, wyświetlenie
    Object.keys(definedAdUnits).length && renderDefinedAdUnits(definedAdUnits);
};

/**
 * Przekazuje slot do osadzenia w zależności od ustawień LL.
 *
 * @param {ServiceSlotType} slotConfig - slot serwisowy do osadzenia
 * @param {string[]} placementIdsToInsert - placementy gdzie osadzone mają być reklamy
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla slotu
 *
 * @returns {void}
 */
const lazyLoadInsertAd = (slotConfig: ServiceSlotType, placementIdsToInsert: string[], targeting: TargetingType | null): void => {
    const slotToDefined = slotConfig as IDFPServiceSlot;

    if (!isSlotCanBeEmbed(slotToDefined)) return;
    slotToDefined.isLazyLoading
        ? embedAdSlotWhenIsVisible(slotToDefined, placementIdsToInsert, targeting)
        : groupExecution(slotConfig, placementIdsToInsert, targeting);
};

/**
 * Odświeża dany slot reklamowy wykorzystując istniejącą jednostkę reklamową.
 *
 * @param {ServiceSlotType} slotConfig - slot serwisowy
 * @param {string[]} placementNamesToRefresh - nazwy placementów znajdujących się w viewport do odświeżenia
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla odświeżanej reklamy
 * @returns {void}
 */
const refreshAd = async (
    slotConfig: ServiceSlotType,
    placementNamesToRefresh: string[],
    targeting: TargetingType | null = null
): Promise<void> => {
    const { adUnits } = window.Inpl.Ad.services?.DFPService as IDFPService;
    const slotToRefresh = slotConfig as IDFPServiceSlot;
    if (!isSlotCanBeEmbed(slotToRefresh)) return;

    // nazwy placementów tylko istniejących jednostek reklamowych
    const placementsToRefresh = placementNamesToRefresh.filter(
        (placementName) => adUnits[placementName] && slotToRefresh.renderedPlacements?.[placementName]
    );

    if (placementsToRefresh.length) {
        const adUnitsToRefresh: googletag.Slot[] = [];
        placementsToRefresh.forEach((placementName) => {
            const isAdUnitExist = adUnits[placementName];
            if (isAdUnitExist) {
                adUnitsToRefresh.push(isAdUnitExist);
                googletag.cmd.push(() => {
                    // ustawienie targetingu dla reklamy
                    targeting && setTargetingBeforeRefresh(slotToRefresh.name, isAdUnitExist, targeting);
                });
            }
        });

        await requestForBids({ [slotConfig.name]: placementsToRefresh });

        googletag.cmd.push(() => {
            // odświeżenie reklamy
            googletag.pubads().refresh(adUnitsToRefresh);
        });
    }
};

/**
 * Odświeża wskazane sloty reklamowe - istniejące, znajdujące się aktualnie w viewport użytkownika.
 *
 * @param {string[]} adUnitNamesToRefresh - nazwy jednostek reklamowych / placementów do odświeżenia
 * @param {TargetingType | null} targeting - opcjonalnie, ustawienie targetowania dla odświeżanych reklam
 * @returns {void}
 */
const refreshAds = async (adUnitNamesToRefresh: string[], targeting: TargetingType | null = null): Promise<void> => {
    const { services, UANC } = window.Inpl.Ad;
    const { adUnits, slots } = services?.DFPService as IDFPService;

    const definedAdUnitsToRefresh: IDefinedAdUnits = adUnitNamesToRefresh.reduce((ads: IDefinedAdUnits, placement: string) => {
        const adUnit = adUnits[placement];
        const slotConfig = (slots as IDFPServiceSlot[]).find((slot) => slot.renderedPlacements && placement in slot.renderedPlacements);
        if (adUnit && slotConfig) {
            const slotAdUnits = slotConfig.name in ads ? ads[slotConfig.name] : [];
            slotAdUnits.push(placement);
            return {
                ...ads,
                [slotConfig.name]: slotAdUnits,
            };
        }

        UANC instanceof DebugUANC && UANC.warn(`[refreshAds] Failed for placement: ${placement} - adUnit or slot does not exist.`);
        return ads;
    }, {});

    const adUnitsToRefresh: googletag.Slot[] = [];
    Object.keys(definedAdUnitsToRefresh).forEach((slotName) => {
        definedAdUnitsToRefresh[slotName].forEach((adUnitName) => {
            const adUnit: googletag.Slot = adUnits[adUnitName];
            if (adUnit) {
                adUnitsToRefresh.push(adUnit);
                googletag.cmd.push(() => {
                    // ustawienie targetingu dla reklamy
                    targeting && setTargetingBeforeRefresh(slotName, adUnit, targeting);
                });
            }
        });
    });

    await requestForBids(definedAdUnitsToRefresh);
    googletag.cmd.push(() => {
        // odświeżenie reklamy
        googletag.pubads().refresh(adUnitsToRefresh);
    });
};

/**
 * Usuwa jednostkę reklamową - przekazanie slotu do GPT.destroySlots, usuwa obiekt definicji w adUnits oraz informację z renderedPlacements.
 *
 * @param {ServiceSlotType} slotConfig - slot serwisowy do osadzenia
 * @param {string | null} placementIdToDestroy - opcjonalnie, nazwa id kontenera, który ma zostać usunięty
 * @returns {void}
 */
const destroyAd = (slotConfig: ServiceSlotType, placementIdToDestroy: string | null): void => {
    const { services, UANC } = window.Inpl.Ad;
    const { adUnits, inView } = services?.DFPService as IDFPService;

    const slotToDestroy = slotConfig as IDFPServiceSlot;
    const placementsToCheck: string[] = placementIdToDestroy ? [placementIdToDestroy] : slotToDestroy.placementIds;
    const placementsToDestroy = getPlacementNamesToDestroy(slotToDestroy, placementsToCheck);

    if (!placementsToDestroy.length) {
        UANC instanceof DebugUANC && UANC.warn(`[destroyAd] No placement to destroy for slot ${slotConfig.name}.`);
        return;
    }

    googletag.cmd.push(() => {
        const adUnitsToDestroy: googletag.Slot[] = placementsToDestroy.map((placementName: string) => adUnits[placementName]);
        googletag.destroySlots(adUnitsToDestroy);
        placementsToDestroy.forEach((placementName: string) => {
            delete inView[placementName];
            delete adUnits[placementName];
            delete slotToDestroy.renderedPlacements?.[placementName];
            UANC instanceof DebugUANC && UANC.success(`[destroyAd] Placement ${placementName} destroyed successfully.`);
        });
    });
};

/**
 * Zapisuje do serwisu DFP konfigurację reklamy dla artykułu sponsorowanego / linku sponsorowanego.
 *
 * @param {string} name - nazwa - linkSpons
 * @param {IArtSponsParams | null} data - konfiguracja
 * @returns {void}
 */
export const setArtSponsParams = (name: string, data: IArtSponsParams | null): void =>
    setArtSponsParamsFromService(`${AD_SERVERS.DFP}Service`, name, data, true);

/**
 * Przekazanie konfiguracji o reklamie dla wskazanego linkSpons.
 *
 * @param {string} name - nazwa - linkSpons
 * @param {(data: IArtSponsParams | null) => void | null} cb - opcjonalnie, cb do wywołania dla linkSpons
 * @returns {IArtSponsParams | null} informacja o reklamie
 */
const getArtSponsParams = (name: string, cb: ((data: IArtSponsParams | null) => void) | null = null): IArtSponsParams | null =>
    getArtSponsParamsFromService(`${AD_SERVERS.DFP}Service`, name, cb);

/**
 * Ustawia targetowanie dla serwera GAM.
 *
 * @param {string} key - klucz targetowania
 * @param {string | string[]} params - wartości parametru targetowania
 * @returns {void}
 */
const setTargeting = (key: string, params: TargetingParamsType): void => {
    try {
        checkTargetingSetting([key, params]);
        googletag.cmd.push(() => googletag.pubads().setTargeting(key, params));
    } catch (error) {
        const { Ad } = window.Inpl;
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(`[setTargeting] Problem with targeting setting. ${error instanceof Error ? error.message : error}`);
    }
};

/**
 * Zwraca parametry ustawione dla wskazanego klucza targetowania - dla serwera GAM.
 *
 * @param {string} key - klucz targetowania
 * @returns {string[]}
 */
const getTargeting = (key: string): string[] | null => {
    try {
        return googletag.pubads().getTargeting(key);
    } catch (error) {
        const { Ad } = window.Inpl;
        Ad.UANC instanceof DebugUANC &&
            Ad.UANC.warn(
                `[getTargeting] Problem with reading targeting params for key: ${key}. ${error instanceof Error ? error.message : error}`
            );
        return null;
    }
};

export const serviceDFPUtils: IServiceDFPUtils = {
    insertAds,
    lazyLoadInsertAd,
    refreshAd,
    refreshAds,
    destroyAd,
    setArtSponsParams,
    getArtSponsParams,
    setTargeting,
    getTargeting,
};
